import React from 'react';
import {
  useHasAccess,
  useProvider,
  useCreatedPartnerPortalChecklist,
  useNormalChecklist,
  usePartnerPortalChecklist,
} from '~/Hooks';
import { PartnerWelcomeModal } from '~/Screens/EventList/PartnerWelcomeModal';
import { ChecklistRequestApproval } from './components';

export enum ChecklistType {
  NORMAL = 'normal',
  PARTNER_PORTAL = 'partnergram',
  CREATED_PARTNER_PORTAL = 'createdPartner',
  UNDEFINED = 'undefined',
}

export const useChecklistComponent = () => {
  const { hasSuperAdminAccess, hasPartnergramSupportAgentAccess } = useHasAccess();
  const {
    activeIndex: normalActiveIndex,
    normalChecklistItems,
    isCheckListLoading,
    checklistError,
    checklist: normalChecklist,
    checklistFooter: normalChecklistFooter,
    updateNormalChecklist,
  } = useNormalChecklist();

  const {
    partnerPortalActiveIndex,
    partnerPortalChecklistItems,
    partnerPortalChecklist,
    updatePartnerPortalChecklist,
  } = usePartnerPortalChecklist();

  const {
    createdPartnerPortalActiveIndex,
    createdPartnerPortalChecklistItems,
    createdPartnerChecklist,
    updateCreatedPartnerChecklist,
    isModalOpen,
    setIsModalOpen,
  } = useCreatedPartnerPortalChecklist();

  const { currentProvider } = useProvider();

  const onClosePartnerWelcomeMessage = () => {
    updatePartnerPortalChecklist({ welcomeMessageVisible: new Date().toISOString() });
  };

  if (partnerPortalChecklist) {
    return {
      activeIndex: partnerPortalActiveIndex,
      checklistItems: partnerPortalChecklistItems,
      checklist: partnerPortalChecklist,
      checklistError,
      isCheckListLoading,
      isChecklistDone: partnerPortalChecklist.checklistHidden,
      updateChecklist: updatePartnerPortalChecklist,
      checklistType: ChecklistType.PARTNER_PORTAL,
      extraComponent: (
        <PartnerWelcomeModal
          open={
            !hasPartnergramSupportAgentAccess &&
            !hasSuperAdminAccess &&
            !partnerPortalChecklist.welcomeMessageVisible
          }
          onClose={onClosePartnerWelcomeMessage}
        />
      ),
    };
  }

  const isCreatedPartnerChecklistDone =
    createdPartnerChecklist?.checklistHidden ||
    (createdPartnerChecklist?.studioDataEdited && createdPartnerChecklist?.eventCreated);

  if (createdPartnerChecklist) {
    return {
      activeIndex: createdPartnerPortalActiveIndex,
      checklistItems: createdPartnerPortalChecklistItems,
      checklist: createdPartnerChecklist,
      checklistError,
      isCheckListLoading,
      isChecklistDone: isCreatedPartnerChecklistDone,
      updateChecklist: updateCreatedPartnerChecklist,
      checklistType: ChecklistType.CREATED_PARTNER_PORTAL,
      extraComponent: (
        <ChecklistRequestApproval
          checklist={createdPartnerChecklist}
          updateChecklist={updateCreatedPartnerChecklist}
          isModalOpen={isModalOpen}
          setIsModalOpen={setIsModalOpen}
        />
      ),
    };
  }

  const isChecklistDone =
    normalChecklist?.isCompleted || currentProvider?.isUSCMember || currentProvider?.isOneFitMember;

  if (normalChecklist) {
    return {
      activeIndex: normalActiveIndex,
      checklistItems: normalChecklistItems,
      checklist: normalChecklist,
      checklistError,
      isCheckListLoading,
      isChecklistDone,
      checklistFooter: normalChecklistFooter,
      updateChecklist: updateNormalChecklist,
      checklistType: ChecklistType.NORMAL,
      extraComponent: null,
    };
  }

  return {
    activeIndex: 0,
    checklistItems: [],
    checklist: undefined,
    checklistError,
    isCheckListLoading: false,
    isChecklistDone: true,
    checklistFooter: undefined,
    updateChecklist: () => {},
    checklistType: ChecklistType.UNDEFINED,
    extraComponent: null,
  };
};
